import type { ReactNode } from 'react';
import { useInvalidUntilFocus } from './useInvalidUntilFocus';

/**
 * FormField — port from ~/FrontendFinal/Design/src/components/FormField.tsx.
 *
 * A label above a bordered box: the wizard's most repeated field primitive. The
 * geometry (heights, the label→field gap via `gap-field-gap`, the border, the
 * transparent fill) is VERBATIM from the design; see the Design repo's header
 * for the SVG provenance of every measure.
 *
 * ONE ADDITION for backend wiring: the Design's FormField was non-functional (a
 * flagged TODO — no value/onChange). The reskin needs real inputs, so this port
 * adds OPTIONAL `value`/`onChange`/`invalid`/`describedBy`/`autoComplete`/`type`
 * props that pass straight through to the input/textarea. When `children` is
 * given (a read-only review row, or a custom control like a <select>) they are
 * ignored, exactly as before. Everything else is unchanged.
 */

export interface FormFieldProps {
  /** Verbatim from the design, asterisk included. */
  label: string;
  /** The field's identity — becomes the input id + name (label htmlFor). */
  name: string;
  /** `tall` is a multi-line description (71); `action` is P3's rows (63). */
  size?: 'default' | 'tall' | 'action';
  /** Spans both tracks of `grid-cols-form`. */
  span?: boolean;
  required?: boolean;
  /** Rendered instead of an input, for read-only review rows / custom controls. */
  children?: ReactNode;
  className?: string;

  /* --- Backend wiring (additions; the Design version had none) ------------- */
  /** Controlled value. Omit to leave the field uncontrolled (Design behaviour). */
  value?: string;
  /** Change handler; receives the raw string value. */
  onChange?: (value: string) => void;
  /** Marks the control aria-invalid (validation error). */
  invalid?: boolean;
  /** id of an error/hint element for aria-describedby. */
  describedBy?: string;
  /** Input type for the single-line control (default 'text'). */
  type?: string;
  autoComplete?: string;
  placeholder?: string;
  /** Registers the DOM node with the Vm's field map (focus-on-error). */
  registerRef?: (el: HTMLInputElement | HTMLTextAreaElement | null) => void;
}

const HEIGHT = {
  default: 'h-field',
  tall: 'h-field-tall',
  action: 'h-field-action',
} as const;

export function FormField({
  label,
  name,
  size = 'default',
  span = false,
  required = false,
  children,
  className = '',
  value,
  onChange,
  invalid,
  describedBy,
  type = 'text',
  autoComplete,
  placeholder,
  registerRef,
}: FormFieldProps) {
  const multiline = size === 'tall' || size === 'action';

  // Red (label + border) persists until the user mouses back into this field —
  // see the hook. `onPointerDown` sits on the wrapper below so it fires for the
  // input/textarea path AND for `children` fields (the FK <select>s, whose own
  // border is driven by SelectControl's copy of this hook); a press anywhere in
  // the field clears the LABEL's red here.
  const { showInvalid, onPointerDown } = useInvalidUntilFocus(invalid);

  return (
    <div
      onPointerDown={onPointerDown}
      className={['flex min-w-0 flex-col gap-field-gap', span ? 'col-span-2' : '', className].join(
        ' ',
      )}
    >
      <label
        htmlFor={name}
        className={[
          'text-xs font-medium',
          // Label reddens with the border when invalid, clears on focus.
          showInvalid ? 'text-error' : 'text-ink',
        ].join(' ')}
      >
        {label}
      </label>

      {children ??
        (multiline ? (
          <textarea
            id={name}
            name={name}
            required={required}
            ref={registerRef}
            {...(value !== undefined ? { value } : {})}
            {...(onChange ? { onChange: (e) => onChange(e.target.value) } : {})}
            {...(invalid ? { 'aria-invalid': true } : {})}
            {...(describedBy ? { 'aria-describedby': describedBy } : {})}
            {...(placeholder ? { placeholder } : {})}
            className={[
              HEIGHT[size],
              'w-full min-w-0 resize-none rounded-md border bg-transparent',
              // Red at rest while invalid, until the user mouses back into the
              // field (see useInvalidUntilFocus). Focus deepens to the ring colour.
              showInvalid ? 'border-error' : 'border-border-subtle',
              'px-field-x py-field-x text-xs font-light text-ink',
              'outline-none focus-visible:border-ring-focus',
            ].join(' ')}
          />
        ) : (
          <input
            id={name}
            name={name}
            type={type}
            required={required}
            ref={registerRef}
            {...(value !== undefined ? { value } : {})}
            {...(onChange ? { onChange: (e) => onChange(e.target.value) } : {})}
            {...(invalid ? { 'aria-invalid': true } : {})}
            {...(describedBy ? { 'aria-describedby': describedBy } : {})}
            {...(autoComplete ? { autoComplete } : {})}
            {...(placeholder ? { placeholder } : {})}
            className={[
              HEIGHT[size],
              'w-full min-w-0 rounded-md border bg-transparent',
              // Red at rest while invalid, until the user mouses back into the
              // field (see useInvalidUntilFocus). Focus deepens to the ring colour.
              showInvalid ? 'border-error' : 'border-border-subtle',
              'px-field-x text-xs font-light text-ink',
              'outline-none focus-visible:border-ring-focus',
            ].join(' ')}
          />
        ))}
    </div>
  );
}
